import { useEffect, useCallback } from "react";
import { useId } from "@fluentui/react-hooks";
import { useTranslation } from "react-i18next";
import { ITextFieldProps, ICheckboxProps, Dropdown, IDropdownProps, IDropdownOption, DefaultButton, IRenderFunction, ISelectableDroppableTextProps } from "@fluentui/react";
import { HelpCallout } from "../HelpCallout";
import styles from "./Filters.module.css";

// Add type for onRenderLabel
type RenderLabelType = ITextFieldProps | IDropdownProps | ICheckboxProps;

// Custom render function for year dropdown - 3 columns, top to bottom flow
const renderYearList = (
    props: ISelectableDroppableTextProps<any, HTMLDivElement> | undefined,
    defaultRender: IRenderFunction<ISelectableDroppableTextProps<any, HTMLDivElement>> | undefined,
    selectedKey: string,
    onChange: (key: string) => void
): JSX.Element | null => {
    if (!props?.options) return null;

    const options = props.options;
    const itemsPerColumn = Math.ceil(options.length / 3);

    // Split into 3 columns (top to bottom flow)
    const column1 = options.slice(0, itemsPerColumn);
    const column2 = options.slice(itemsPerColumn, itemsPerColumn * 2);
    const column3 = options.slice(itemsPerColumn * 2);

    const renderItem = (option: IDropdownOption) => {
        const isSelected = option.key === selectedKey;
        return (
            <button
                key={option.key}
                onClick={() => onChange(option.key as string)}
                className={styles.yearDropdownItem}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 8px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    width: "100%",
                    textAlign: "left",
                    fontSize: "14px",
                    color: isSelected ? "#0468B1" : "#232E3D"
                }}
            >
                <span style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    border: isSelected ? "none" : "2px solid #A9B1B7",
                    background: isSelected ? "#0468B1" : "transparent",
                    flexShrink: 0
                }} />
                {option.text}
            </button>
        );
    };

    return (
        <div style={{
            display: "flex",
            padding: "8px 16px",
            minWidth: "350px"
        }}>
            <div style={{ flex: 1 }}>{column1.map(renderItem)}</div>
            <div style={{ flex: 1 }}>{column2.map(renderItem)}</div>
            <div style={{ flex: 1 }}>{column3.map(renderItem)}</div>
        </div>
    );
};

export interface FiltersProps {
    className?: string;
    country: string[];
    region: string[];
    regionalBureau: string[];
    evaluationType: string[];
    qaRating: string[];
    sectionTypes: string[];
    projectType: string;
    sdgGoals: string;
    jointEvaluation: string;
    gefEvaluation: string;
    yearFrom: string;
    yearTo: string;
    onChange: (field: string, value: any) => void;
    onReset?: () => void;
    scrollToId?: string;
}
// Dropdown filter options
export const evaluationTypeOptions: IDropdownOption[] = [
    { key: "Project", text: "Project" },
    { key: "Outcome", text: "Outcome" },
    { key: "ICPE/ADR", text: "ICPE/ADR" },
    { key: "Others", text: "Others" },
    { key: "Thematic", text: "Thematic" }
];

export const countryOptions: IDropdownOption[] = [
    { key: "AFGHANISTAN", text: "AFGHANISTAN" },
    { key: "ALBANIA", text: "ALBANIA" },
    { key: "ALGERIA", text: "ALGERIA" },
    { key: "ANGOLA", text: "ANGOLA" },
    { key: "ANGUILLA", text: "ANGUILLA" },
    { key: "ARGENTINA", text: "ARGENTINA" },
    { key: "ARMENIA", text: "ARMENIA" },
    { key: "ARUBA", text: "ARUBA" },
    { key: "AZERBAIJAN", text: "AZERBAIJAN" },
    { key: "BAHAMAS", text: "BAHAMAS" },
    { key: "BAHRAIN", text: "BAHRAIN" },
    { key: "BANGLADESH", text: "BANGLADESH" },
    { key: "BARBADOS", text: "BARBADOS" },
    { key: "BELARUS, REPUBLIC OF", text: "BELARUS, REPUBLIC OF" },
    { key: "BELIZE", text: "BELIZE" },
    { key: "BENIN", text: "BENIN" },
    { key: "BERMUDA", text: "BERMUDA" },
    { key: "BHUTAN", text: "BHUTAN" },
    { key: "BOLIVIA", text: "BOLIVIA" },
    { key: "BOSNIA AND HERCEGOVINA", text: "BOSNIA AND HERCEGOVINA" },
    { key: "BOTSWANA", text: "BOTSWANA" },
    { key: "BRAZIL", text: "BRAZIL" },
    { key: "BRUNEI DARUSSALAM", text: "BRUNEI DARUSSALAM" },
    { key: "BULGARIA", text: "BULGARIA" },
    { key: "BURKINA FASO", text: "BURKINA FASO" },
    { key: "BURMA", text: "BURMA" },
    { key: "BURUNDI", text: "BURUNDI" },
    { key: "CAMBODIA", text: "CAMBODIA" },
    { key: "CAMEROON, REPUBLIC OF", text: "CAMEROON, REPUBLIC OF" },
    { key: "CAPE VERDE", text: "CAPE VERDE" },
    { key: "CARIBBEAN (REGIONAL)", text: "CARIBBEAN (REGIONAL)" },
    { key: "CAYMAN ISLANDS", text: "CAYMAN ISLANDS" },
    { key: "CENTRAL AFRICAN REPUBLIC", text: "CENTRAL AFRICAN REPUBLIC" },
    { key: "CENTRAL AMERICA", text: "CENTRAL AMERICA" },
    { key: "CHAD", text: "CHAD" },
    { key: "CHILE", text: "CHILE" },
    { key: "CHINA, PEOPLE'S REPUBLIC", text: "CHINA, PEOPLE'S REPUBLIC" },
    { key: "COLOMBIA", text: "COLOMBIA" },
    { key: "COMOROS", text: "COMOROS" },
    { key: "CONGO BRAZZAVILLE", text: "CONGO BRAZZAVILLE" },
    { key: "CONGO DRC", text: "CONGO DRC" },
    { key: "COOK ISLANDS", text: "COOK ISLANDS" },
    { key: "COSTA RICA", text: "COSTA RICA" },
    { key: "COTE d'IVOIRE", text: "COTE d'IVOIRE" },
    { key: "CROATIA", text: "CROATIA" },
    { key: "CUBA", text: "CUBA" },
    { key: "CYPRUS", text: "CYPRUS" },
    { key: "CZECH REPUBLIC", text: "CZECH REPUBLIC" },
    { key: "DAHOMEY", text: "DAHOMEY" },
    { key: "DENMARK", text: "DENMARK" },
    { key: "DJIBOUTI", text: "DJIBOUTI" },
    { key: "DOMINICA", text: "DOMINICA" },
    { key: "DOMINICAN REPUBLIC", text: "DOMINICAN REPUBLIC" },
    { key: "DPRK -DEMOCRATIC PEOPLE'S REPUBLIC OF KOREA", text: "DPRK -DEMOCRATIC PEOPLE'S REPUBLIC OF KOREA" },
    { key: "ECUADOR", text: "ECUADOR" },
    { key: "EGYPT", text: "EGYPT" },
    { key: "EL SALVADOR", text: "EL SALVADOR" },
    { key: "EQUATORIAL GUINEA", text: "EQUATORIAL GUINEA" },
    { key: "ERITREA", text: "ERITREA" },
    { key: "ESTONIA, REPUBLIC OF", text: "ESTONIA, REPUBLIC OF" },
    { key: "ESWATINI", text: "ESWATINI" },
    { key: "ETHIOPIA", text: "ETHIOPIA" },
    { key: "FIJI", text: "FIJI" },
    { key: "FINLAND", text: "FINLAND" },
    { key: "FRANCE", text: "FRANCE" },
    { key: "GABON", text: "GABON" },
    { key: "GAMBIA", text: "GAMBIA" },
    { key: "GEORGIA, REPUBLIC OF", text: "GEORGIA, REPUBLIC OF" },
    { key: "GERMANY", text: "GERMANY" },
    { key: "GHANA", text: "GHANA" },
    { key: "GILBERT ISLANDS", text: "GILBERT ISLANDS" },
    { key: "GREECE", text: "GREECE" },
    { key: "GRENADA", text: "GRENADA" },
    { key: "GUADELOUPE", text: "GUADELOUPE" },
    { key: "GUATEMALA", text: "GUATEMALA" },
    { key: "GUINEA", text: "GUINEA" },
    { key: "GUINEA (FRENCH)", text: "GUINEA (FRENCH)" },
    { key: "GUINEA-BISSAU", text: "GUINEA-BISSAU" },
    { key: "GUYANA", text: "GUYANA" },
    { key: "HAITI", text: "HAITI" },
    { key: "HONDURAS", text: "HONDURAS" },
    { key: "HUNGARY", text: "HUNGARY" },
    { key: "ICELAND", text: "ICELAND" },
    { key: "INDIA", text: "INDIA" },
    { key: "INDONESIA", text: "INDONESIA" },
    { key: "INTER-REGIONAL", text: "INTER-REGIONAL" },
    { key: "IRAN (ISLAMIC REPUBLIC OF)", text: "IRAN (ISLAMIC REPUBLIC OF)" },
    { key: "IRAQ", text: "IRAQ" },
    { key: "IRELAND", text: "IRELAND" },
    { key: "ISRAEL", text: "ISRAEL" },
    { key: "ITALY", text: "ITALY" },
    { key: "JAMAICA", text: "JAMAICA" },
    { key: "JAPAN", text: "JAPAN" },
    { key: "JORDAN", text: "JORDAN" },
    { key: "KAMPUCHEA", text: "KAMPUCHEA" },
    { key: "KAZAKHSTAN", text: "KAZAKHSTAN" },
    { key: "KENYA", text: "KENYA" },
    { key: "KHMER REPUBLIC", text: "KHMER REPUBLIC" },
    { key: "KIRIBATI", text: "KIRIBATI" },
    { key: "KOSOVO", text: "KOSOVO" },
    { key: "KUWAIT", text: "KUWAIT" },
    { key: "KYRGYZSTAN", text: "KYRGYZSTAN" },
    { key: "LAO PEOPLE'S DEMOCRATIC REPUBLIC", text: "LAO PEOPLE'S DEMOCRATIC REPUBLIC" },
    { key: "LATVIA, REPUBLIC OF", text: "LATVIA, REPUBLIC OF" },
    { key: "LEBANON", text: "LEBANON" },
    { key: "LESOTHO", text: "LESOTHO" },
    { key: "LIBERIA", text: "LIBERIA" },
    { key: "LIBYA", text: "LIBYA" },
    { key: "LIECHTENSTEIN", text: "LIECHTENSTEIN" },
    { key: "LITHUANIA, REPUBLIC OF", text: "LITHUANIA, REPUBLIC OF" },
    { key: "LUXEMBOURG", text: "LUXEMBOURG" },
    { key: "MACAO", text: "MACAO" },
    { key: "MADAGASCAR", text: "MADAGASCAR" },
    { key: "MALAWI", text: "MALAWI" },
    { key: "MALAYSIA", text: "MALAYSIA" },
    { key: "MALDIVES", text: "MALDIVES" },
    { key: "MALI", text: "MALI" },
    { key: "MALTA", text: "MALTA" },
    { key: "MARSHALL ISLANDS", text: "MARSHALL ISLANDS" },
    { key: "MARTINIQUE", text: "MARTINIQUE" },
    { key: "MAURITANIA", text: "MAURITANIA" },
    { key: "MAURITIUS", text: "MAURITIUS" },
    { key: "MEXICO", text: "MEXICO" },
    { key: "MICRONESIA, THE FEDERATED STATES OF", text: "MICRONESIA, THE FEDERATED STATES OF" },
    { key: "MOLDOVA, REPUBLIC OF", text: "MOLDOVA, REPUBLIC OF" },
    { key: "MONACO", text: "MONACO" },
    { key: "MONGOLIA", text: "MONGOLIA" },
    { key: "MONTENEGRO", text: "MONTENEGRO" },
    { key: "MONTSERRAT", text: "MONTSERRAT" },
    { key: "MOROCCO", text: "MOROCCO" },
    { key: "MOZAMBIQUE", text: "MOZAMBIQUE" },
    { key: "MYANMAR", text: "MYANMAR" },
    { key: "NAMIBIA", text: "NAMIBIA" },
    { key: "NEPAL", text: "NEPAL" },
    { key: "NICARAGUA", text: "NICARAGUA" },
    { key: "NIGER", text: "NIGER" },
    { key: "NIGERIA", text: "NIGERIA" },
    { key: "NORTH MACEDONIA", text: "NORTH MACEDONIA" },
    { key: "OCCUPIED PALESTINIAN TERRITORIES", text: "OCCUPIED PALESTINIAN TERRITORIES" },
    { key: "OMAN", text: "OMAN" },
    { key: "PAKISTAN", text: "PAKISTAN" },
    { key: "PAPUA NEW GUINEA", text: "PAPUA NEW GUINEA" },
    { key: "PARAGUAY", text: "PARAGUAY" },
    { key: "PERU", text: "PERU" },
    { key: "PHILIPPINES", text: "PHILIPPINES" },
    { key: "POLAND", text: "POLAND" },
    { key: "PORTUGAL", text: "PORTUGAL" },
    { key: "PUERTO RICO", text: "PUERTO RICO" },
    { key: "QATAR", text: "QATAR" },
    { key: "RBA", text: "RBA" },
    { key: "RBEC Regional Center", text: "RBEC Regional Center" },
    { key: "REPUBLIC OF KOREA", text: "REPUBLIC OF KOREA" },
    { key: "REPUBLIC OF VIETNAM", text: "REPUBLIC OF VIETNAM" },
    { key: "ROMANIA", text: "ROMANIA" },
    { key: "RUSSIAN FEDERATION", text: "RUSSIAN FEDERATION" },
    { key: "RWANDA", text: "RWANDA" },
    { key: "SAMOA", text: "SAMOA" },
    { key: "SAINT HELENA", text: "SAINT HELENA" },
    { key: "SAO TOME AND PRINCIPE", text: "SAO TOME AND PRINCIPE" },
    { key: "SAUDI ARABIA", text: "SAUDI ARABIA" },
    { key: "SENEGAL", text: "SENEGAL" },
    { key: "SERBIA", text: "SERBIA" },
    { key: "SEYCHELLES", text: "SEYCHELLES" },
    { key: "SIERRA LEONE", text: "SIERRA LEONE" },
    { key: "SLOVAK REPUBLIC", text: "SLOVAK REPUBLIC" },
    { key: "SLOVENIA", text: "SLOVENIA" },
    { key: "SOLOMON ISLANDS", text: "SOLOMON ISLANDS" },
    { key: "SOMALIA", text: "SOMALIA" },
    { key: "SOUTH AFRICA", text: "SOUTH AFRICA" },
    { key: "SOUTH SUDAN", text: "SOUTH SUDAN" },
    { key: "SOUTH WEST AFRIC PEOPLE'S ORGANIZATION", text: "SOUTH WEST AFRIC PEOPLE'S ORGANIZATION" },
    { key: "SRI LANKA", text: "SRI LANKA" },
    { key: "SUDAN", text: "SUDAN" },
    { key: "SURINAME", text: "SURINAME" },
    { key: "SWITZERLAND", text: "SWITZERLAND" },
    { key: "SYRIAN ARAB REPUBLIC", text: "SYRIAN ARAB REPUBLIC" },
    { key: "TAJIKISTAN", text: "TAJIKISTAN" },
    { key: "TANZANIA (UNITED REPUBLIC OF )", text: "TANZANIA (UNITED REPUBLIC OF )" },
    { key: "TERRITORY OF HONG KONG", text: "TERRITORY OF HONG KONG" },
    { key: "THAILAND", text: "THAILAND" },
    { key: "Timor-Leste", text: "Timor-Leste" },
    { key: "TOGO", text: "TOGO" },
    { key: "TONGA", text: "TONGA" },
    { key: "TRINIDAD AND TOBAGO", text: "TRINIDAD AND TOBAGO" },
    { key: "TUNISIA", text: "TUNISIA" },
    { key: "TURKEY", text: "TURKEY" },
    { key: "TURKMENISTAN", text: "TURKMENISTAN" },
    { key: "UGANDA", text: "UGANDA" },
    { key: "UKRAINE", text: "UKRAINE" },
    { key: "UNITED ARAB EMIRATES", text: "UNITED ARAB EMIRATES" },
    { key: "URUGUAY", text: "URUGUAY" },
    { key: "UZBEKISTAN", text: "UZBEKISTAN" },
    { key: "VANUATU", text: "VANUATU" },
    { key: "VENEZUELA", text: "VENEZUELA" },
    { key: "VIET NAM", text: "VIET NAM" },
    { key: "VIRGIN ISLANDS (UK)", text: "VIRGIN ISLANDS (UK)" },
    { key: "YEMEN", text: "YEMEN" },
    { key: "YEMEN, DEMOCRATIC", text: "YEMEN, DEMOCRATIC" },
    { key: "ZAMBIA", text: "ZAMBIA" },
    { key: "ZIMBABWE", text: "ZIMBABWE" }
];

export const regionOptions: IDropdownOption[] = [
    { key: "GLO", text: "Global" },
    { key: "RBA", text: "Africa" },
    { key: "RBAP", text: "Asia and the Pacific" },
    { key: "RBAS", text: "Arab States" },
    { key: "RBLAC", text: "Latin America and the Caribbean" },
    { key: "RBEC", text: "Europe and the Commonwealth of Independent States (CIS)" }
];

export const regionalBureauOptions: IDropdownOption[] = [
    // Regional Bureaus and HQ Units
    { key: "RBA", text: "Regional Bureau for Africa" },
    { key: "RBLAC", text: "Regional Bureau for Latin America and the Caribbean" },
    { key: "RBAP", text: "Regional Bureau for Asia and the Pacific" },
    { key: "RBAS", text: "Regional Bureau for Arab States" },
    { key: "RBEC", text: "Regional Bureau for Europe and CIS" }, // ✅ ADDED - was missing
    { key: "Independent Evaluation Office", text: "Independent Evaluation Office" },
    { key: "Executive Office", text: "Executive Office" },
    { key: "UNCDF", text: "UN Capital Development Fund" }, // ✅ FIXED - was "UN Capital Development Fund" as key
    { key: "Bureau for Policy and Programme Support", text: "Bureau for Policy and Programme Support" },
    { key: "Crisis Bureau", text: "Crisis Bureau" },
    { key: "UNV", text: "UN Volunteers" }, // ✅ ADDED - was missing
    { key: "UN Millennium Campaign Unit", text: "UN Millennium Campaign Unit" }, // ✅ ADDED - was missing
    { key: "UN Office for South-South Cooperation", text: "UN Office for South-South Cooperation" }, // ✅ ADDED - was missing

    // Countries
    { key: "Aruba", text: "ARUBA" },
    { key: "Afghanistan", text: "AFGHANISTAN" },
    { key: "Angola", text: "ANGOLA" },
    { key: "Anguilla", text: "ANGUILLA" },
    { key: "Albania", text: "ALBANIA" },
    { key: "United Arab Emirates", text: "UNITED ARAB EMIRATES" },
    { key: "Argentina", text: "ARGENTINA" },
    { key: "Armenia", text: "ARMENIA" },
    { key: "Azerbaijan", text: "AZERBAIJAN" },
    { key: "Burundi", text: "BURUNDI" },
    { key: "Benin", text: "BENIN" },
    { key: "Burkina Faso", text: "BURKINA FASO" },
    { key: "Bangladesh", text: "BANGLADESH" },
    { key: "Bulgaria", text: "BULGARIA" },
    { key: "Bahrain", text: "BAHRAIN" },
    { key: "Bahamas", text: "BAHAMAS" },
    { key: "Bosnia and Hercegovina", text: "BOSNIA AND HERCEGOVINA" }, // ✅ FIXED - was "Bosnia and Herzegovina"
    { key: "Belarus", text: "BELARUS, REPUBLIC OF" },
    { key: "Belize", text: "BELIZE" },
    { key: "Bermuda", text: "BERMUDA" },
    { key: "Bolivia", text: "BOLIVIA" },
    { key: "Brazil", text: "BRAZIL" },
    { key: "Barbados", text: "BARBADOS" },
    { key: "RBEC Regional Center", text: "RBEC Regional Center" },
    { key: "Brunei Darussalam", text: "BRUNEI DARUSSALAM" },
    { key: "Bhutan", text: "BHUTAN" },
    { key: "Burma", text: "BURMA" },
    { key: "Botswana", text: "BOTSWANA" },
    { key: "Central African Republic", text: "CENTRAL AFRICAN REPUBLIC" },
    { key: "Central America", text: "CENTRAL AMERICA" },
    { key: "Switzerland", text: "SWITZERLAND" },
    { key: "Chile", text: "CHILE" },
    { key: "China", text: "CHINA, PEOPLE'S REPUBLIC" }, // ✅ FIXED - was "China, People's Republic" as key
    { key: "Côte d'Ivoire", text: "COTE d'IVOIRE" }, // ✅ FIXED - was "Cote d'Ivoire"
    { key: "Cameroon", text: "CAMEROON, REPUBLIC OF" }, // ✅ FIXED - was "Cameroon, Republic Of" as key
    { key: "Congo DRC", text: "CONGO DRC" },
    { key: "Congo Brazzaville", text: "CONGO BRAZZAVILLE" },
    { key: "Cook Islands", text: "COOK ISLANDS" },
    { key: "Colombia", text: "COLOMBIA" },
    { key: "Comoros", text: "COMOROS" },
    { key: "Cape Verde", text: "CAPE VERDE" },
    { key: "Costa Rica", text: "COSTA RICA" },
    { key: "Cuba", text: "CUBA" },
    { key: "Cayman Islands", text: "CAYMAN ISLANDS" },
    { key: "Cyprus", text: "CYPRUS" },
    { key: "Czech Republic", text: "CZECH REPUBLIC" },
    { key: "Denmark", text: "DENMARK" },
    { key: "Germany", text: "GERMANY" },
    { key: "Djibouti", text: "DJIBOUTI" },
    { key: "Dominica", text: "DOMINICA" },
    { key: "Dominican Republic", text: "DOMINICAN REPUBLIC" },
    { key: "DPR Korea", text: "DPR KOREA" },
    { key: "Algeria", text: "ALGERIA" },
    { key: "Ecuador", text: "ECUADOR" },
    { key: "Egypt", text: "EGYPT" },
    { key: "Eritrea", text: "ERITREA" },
    { key: "Estonia, Republic Of", text: "ESTONIA, REPUBLIC OF" },
    { key: "Ethiopia", text: "ETHIOPIA" },
    { key: "French Guinea", text: "GUINEA (FRENCH)" },
    { key: "Finland", text: "FINLAND" },
    { key: "Fiji", text: "FIJI" },
    { key: "France", text: "FRANCE" },
    { key: "Gabon", text: "GABON" },
    { key: "Georgia", text: "GEORGIA, REPUBLIC OF" }, // ✅ FIXED - was "Georgia, Republic Of" as key
    { key: "Ghana", text: "GHANA" },
    { key: "Gilbert Islands", text: "GILBERT ISLANDS" },
    { key: "Guinea", text: "GUINEA" },
    { key: "Gambia", text: "GAMBIA" },
    { key: "Guinea-Bissau", text: "GUINEA-BISSAU" },
    { key: "Equatorial Guinea", text: "EQUATORIAL GUINEA" },
    { key: "Greece", text: "GREECE" },
    { key: "Grenada", text: "GRENADA" },
    { key: "Guatemala", text: "GUATEMALA" },
    { key: "Guadeloupe", text: "GUADELOUPE" },
    { key: "Guyana", text: "GUYANA" },
    { key: "Honduras", text: "HONDURAS" },
    { key: "Territory of Hong Kong", text: "TERRITORY OF HONG KONG" },
    { key: "Croatia", text: "CROATIA" },
    { key: "Haiti", text: "HAITI" },
    { key: "Hungary", text: "HUNGARY" },
    { key: "Iceland", text: "ICELAND" },
    { key: "Indonesia", text: "INDONESIA" },
    { key: "India", text: "INDIA" },
    { key: "Ireland", text: "IRELAND" },
    { key: "Iran", text: "IRAN (ISLAMIC REPUBLIC OF)" }, // ✅ FIXED - was "Iran, Islamic Republic Of" as key
    { key: "Iraq", text: "IRAQ" },
    { key: "Israel", text: "ISRAEL" },
    { key: "Italy", text: "ITALY" },
    { key: "Jamaica", text: "JAMAICA" },
    { key: "Jordan", text: "JORDAN" },
    { key: "Japan", text: "JAPAN" },
    { key: "Kazakhstan", text: "KAZAKHSTAN" },
    { key: "Kenya", text: "KENYA" },
    { key: "Kyrgyzstan", text: "KYRGYZSTAN" },
    { key: "Cambodia", text: "CAMBODIA" },
    { key: "Kiribati", text: "KIRIBATI" },
    { key: "South Korea", text: "SOUTH KOREA" },
    { key: "Kosovo", text: "KOSOVO" },
    { key: "Kuwait", text: "KUWAIT" },
    { key: "Lao", text: "LAO PEOPLE'S DEMOCRATIC REPUBLIC" }, // ✅ FIXED - was "Laos"
    { key: "Lebanon", text: "LEBANON" },
    { key: "Liberia", text: "LIBERIA" },
    { key: "Libya", text: "LIBYA" }, // ✅ ADDED - was missing
    { key: "Liechtenstein", text: "LIECHTENSTEIN" },
    { key: "Sri Lanka", text: "SRI LANKA" },
    { key: "Lesotho", text: "LESOTHO" },
    { key: "Lithuana", text: "LITHUANA, REPUBLIC OF" }, // ✅ FIXED - was "Lithuania, Republic Of" (note: source has typo "Lithuana")
    { key: "Luxembourg", text: "LUXEMBOURG" },
    { key: "Latvia, Republic Of", text: "LATVIA, REPUBLIC OF" },
    { key: "Macau", text: "MACAO" },
    { key: "Martinique", text: "MARTINIQUE" },
    { key: "Morocco", text: "MOROCCO" },
    { key: "Monaco", text: "MONACO" },
    { key: "Moldova", text: "MOLDOVA, REPUBLIC OF" }, // ✅ FIXED - was "Moldova, Republic Of" as key
    { key: "Madagascar", text: "MADAGASCAR" },
    { key: "Maldives", text: "MALDIVES" },
    { key: "Mexico", text: "MEXICO" }, // ✅ FIXED - was "MEXICO" as key
    { key: "Marshall Islands", text: "MARSHALL ISLANDS" },
    { key: "Micronesia, The Federated States Of", text: "MICRONESIA, THE FEDERATED STATES OF" },
    { key: "North Macedonia", text: "NORTH MACEDONIA" },
    { key: "Mali", text: "MALI" },
    { key: "Malawi", text: "MALAWI" }, // ✅ FIXED - was duplicate "Mali"
    { key: "Malt", text: "MALTA" },
    { key: "Myanmar", text: "MYANMAR" },
    { key: "Montenegro", text: "MONTENEGRO" },
    { key: "Mongolia", text: "MONGOLIA" },
    { key: "Montserrat", text: "MONTSERRAT" },
    { key: "Mozambique", text: "MOZAMBIQUE" },
    { key: "Mauritania", text: "MAURITANIA" },
    { key: "Mauritius", text: "MAURITIUS" },
    { key: "Malaysia", text: "MALAYSIA" },
    { key: "Namibia", text: "NAMIBIA" },
    { key: "Niger", text: "NIGER" },
    { key: "Nigeria", text: "NIGERIA" },
    { key: "Nicaragua", text: "NICARAGUA" },
    { key: "Nepal", text: "NEPAL" },
    { key: "OMAN", text: "OMAN" },
    { key: "Pakistan", text: "PAKISTAN" },
    { key: "Palestine", text: "PALESTINE" },
    { key: "Panama", text: "PANAMA" },
    { key: "Peru", text: "PERU" },
    { key: "Philippines", text: "PHILIPPINES" },
    { key: "Papua New Guinea", text: "PAPUA NEW GUINEA" },
    { key: "Poland", text: "POLAND" },
    { key: "Portugal", text: "PORTUGAL" },
    { key: "Puerto Rico", text: "PUERTO RICO" },
    { key: "Paraguay", text: "PARAGUAY" },
    { key: "Qatar", text: "QATAR" },
    { key: "Romania", text: "ROMANIA" },
    { key: "Russian Federation", text: "RUSSIAN FEDERATION" },
    { key: "Republic of Vietnam", text: "REPUBLIC OF VIETNAM" },
    { key: "Rwanda", text: "RWANDA" },
    { key: "Saudi Arabia", text: "SAUDI ARABIA" },
    { key: "Sudan", text: "SUDAN" },
    { key: "Senegal", text: "SENEGAL" },
    { key: "Solomon Islands", text: "SOLOMON ISLANDS" },
    { key: "Sierra Leone", text: "SIERRA LEONE" },
    { key: "El Salvador", text: "EL SALVADOR" },
    { key: "Somalia", text: "SOMALIA" },
    { key: "Serbia", text: "SERBIA" },
    { key: "South Sudan", text: "SOUTH SUDAN" },
    { key: "Saint Helena", text: "SAINT HELENA" },
    { key: "Sao Tome and Principe", text: "SAO TOME AND PRINCIPE" },
    { key: "Suriname", text: "SURINAME" },
    { key: "Slovak Republic", text: "SLOVAK REPUBLIC" }, // ✅ FIXED - was "Slovakia"
    { key: "Slovenia", text: "SLOVENIA" },
    { key: "South West Africa People's Organization", text: "SOUTH WEST AFRIC PEOPLE'S ORGANIZATION" },
    { key: "Eswatini", text: "ESWATINI" },
    { key: "Seychelles", text: "SEYCHELLES" },
    { key: "Syria", text: "SYRIAN ARAB REPUBLIC" }, // ✅ FIXED - was "Syrian Arab Republic" as key
    { key: "Chad", text: "CHAD" },
    { key: "Togo", text: "TOGO" },
    { key: "Thailand", text: "THAILAND" },
    { key: "Tajikistan", text: "TAJIKISTAN" },
    { key: "Turkmenistan", text: "TURKMENISTAN" },
    { key: "Timor-Leste", text: "Timor-Leste" },
    { key: "Tonga", text: "TONGA" },
    { key: "Trinidad and Tobago", text: "TRINIDAD AND TOBAGO" },
    { key: "Tunisia", text: "TUNISIA" },
    { key: "Türkiye", text: "TURKEY" }, // ✅ FIXED - was "Turkey"
    { key: "Tanzania", text: "TANZANIA (UNITED REPUBLIC OF )" },
    { key: "Uganda", text: "UGANDA" },
    { key: "Ukraine", text: "UKRAINE" },
    { key: "Uruguay", text: "URUGUAY" },
    { key: "Uzbekistan", text: "UZBEKISTAN" },
    { key: "Venezuela", text: "VENEZUELA" },
    { key: "Viet Nam", text: "VIET NAM" }, // ✅ FIXED - was "Vietnam"
    { key: "Vanuatu", text: "VANUATU" },
    { key: "Samoa", text: "SAMOA" },
    { key: "Yemen", text: "YEMEN" }, // ✅ FIXED - removed duplicate
    { key: "South Africa", text: "SOUTH AFRICA" },
    { key: "Zambia", text: "ZAMBIA" },
    { key: "Zimbabwe", text: "ZIMBABWE" }
];

export const qaRatingOptions: IDropdownOption[] = [
    { key: "Satisfactory", text: "Satisfactory" },
    { key: "Moderately Satisfactory", text: "Moderately Satisfactory" },
    { key: "Moderately Unsatisfactory", text: "Moderately Unsatisfactory" },
    { key: "Unsatisfactory", text: "Unsatisfactory" }
];

export const sectionTypeOptions: IDropdownOption[] = [
    { key: "context_background_purpose", text: "Context / Background / Purpose" },
    { key: "methodology", text: "Methodology" },
    { key: "findings", text: "Findings" },
    { key: "conclusions", text: "Conclusions" },
    { key: "recommendations", text: "Recommendations" },
    { key: "executive_summary", text: "Executive Summary" },
    { key: "terms_of_reference", text: "Terms of Reference" },
    { key: "annexes", text: "Annexes" },
    { key: "other", text: "Other" }
];

export const reportLanguageOptions: IDropdownOption[] = [
    { key: "", text: "" },
    { key: "English", text: "English" },
    { key: "French", text: "French" },
    { key: "Spanish", text: "Spanish" },
    { key: "Portuguese", text: "Portuguese" },
    { key: "Arabic", text: "Arabic" },
    { key: "Chinese", text: "Chinese" },
    { key: "Russian", text: "Russian" },
    { key: "Other", text: "Other" }
    // Add more languages as needed
];

export const gefEvaluationOptions: IDropdownOption[] = [
    { key: "Yes", text: "Yes" },
    { key: "No", text: "No" }
];

export const jointEvaluationOptions: IDropdownOption[] = [
    { key: "Yes", text: "Yes" },
    { key: "No", text: "No" }
];

export const projectTypeOptions: IDropdownOption[] = [
    { key: "", text: "" },
    { key: "Atlas Project", text: "Atlas Project" },
    { key: "Quantum Project", text: "Quantum Project" }
];

export const sdgGoalsOptions: IDropdownOption[] = [
    { key: "", text: "" },
    { key: "SDG1", text: "SDG1" },
    { key: "SDG2", text: "SDG2" },
    { key: "SDG3", text: "SDG3" },
    { key: "SDG4", text: "SDG4" },
    { key: "SDG5", text: "SDG5" },
    { key: "SDG6", text: "SDG6" },
    { key: "SDG7", text: "SDG7" },
    { key: "SDG8", text: "SDG8" },
    { key: "SDG9", text: "SDG9" },
    { key: "SDG10", text: "SDG10" },
    { key: "SDG11", text: "SDG11" },
    { key: "SDG12", text: "SDG12" },
    { key: "SDG13", text: "SDG13" },
    { key: "SDG14", text: "SDG14" },
    { key: "SDG15", text: "SDG15" },
    { key: "SDG16", text: "SDG16" },
    { key: "SDG17", text: "SDG17" }
];

// Generate year options from 1999 to current year
const currentYear = new Date().getFullYear();
export const yearOptions: IDropdownOption[] = Array.from({ length: currentYear - 1999 + 1 }, (_, i) => {
    const year = (currentYear - i).toString();
    return { key: year, text: year };
});

export const Filters = ({
    country,
    region,
    regionalBureau,
    evaluationType,
    qaRating,
    sectionTypes,
    projectType,
    sdgGoals,
    jointEvaluation,
    gefEvaluation,
    yearFrom,
    yearTo,
    className,
    onChange,
    onReset,
    scrollToId
}: FiltersProps) => {
    const { t } = useTranslation();

    const countryId = useId("country");
    const countryFieldId = useId("countryField");
    const regionId = useId("region");
    const regionFieldId = useId("regionField");
    const regionalBureauId = useId("regionalBureau");
    const regionalBureauFieldId = useId("regionalBureauField");
    const evaluationTypeId = useId("evaluationType");
    const evaluationTypeFieldId = useId("evaluationTypeField");
    const qaRatingId = useId("qaRating");
    const qaRatingFieldId = useId("qaRatingField");

    const sectionTypesId = useId("sectionTypes");
    const sectionTypesFieldId = useId("sectionTypesField");

    const projectTypeId = useId("projectType");
    const projectTypeFieldId = useId("projectTypeField");

    const sdgGoalsId = useId("sdgGoals");
    const sdgGoalsFieldId = useId("sdgGoalsField");

    const jointEvaluationId = useId("jointEvaluation");
    const jointEvaluationFieldId = useId("jointEvaluationField");

    const gefEvaluationId = useId("gefEvaluation");
    const gefEvaluationFieldId = useId("gefEvaluationField");

    const yearFromId = useId("yearFrom");
    const yearFromFieldId = useId("yearFromField");
    const yearToId = useId("yearTo");
    const yearToFieldId = useId("yearToField");

    const renderLabel = (props: RenderLabelType | undefined, labelId: string, fieldId: string, helpText: string) => (
        <HelpCallout labelId={labelId} fieldId={fieldId} helpText={""} label={props?.label} />
    );

    useEffect(() => {
        if (!scrollToId) return;
        const target = document.getElementById(scrollToId);
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [scrollToId]);

    return (
        <div className={className}>
            <div id="filter-region">
                <Dropdown
                    id={regionFieldId}
                    className={styles.settingsSeparator}
                    label={t("filters.region")}
                selectedKeys={region}
                multiSelect
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => {
                    if (option) {
                        const currentSelection = region || [];
                        let newSelection: string[];

                        if (option.selected) {
                            // Add the option if it's being selected
                            newSelection = [...currentSelection, option.key as string];
                        } else {
                            // Remove the option if it's being deselected
                            newSelection = currentSelection.filter(key => key !== option.key);
                        }

                        onChange("region", newSelection);
                    }
                }}
                aria-labelledby={regionId}
                options={regionOptions}
                onRenderLabel={props => renderLabel(props, regionId, regionFieldId, t("helpTexts.includeCategory"))}
                />
            </div>

            <div id="filter-programme-unit">
                <Dropdown
                id={regionalBureauFieldId}
                className={styles.settingsSeparator}
                label={t("filters.regionalBureau")}
                selectedKeys={regionalBureau}
                multiSelect
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => {
                    if (option) {
                        const currentSelection = regionalBureau || [];
                        let newSelection: string[];

                        if (option.selected) {
                            // Add the option if it's being selected
                            newSelection = [...currentSelection, option.key as string];
                        } else {
                            // Remove the option if it's being deselected
                            newSelection = currentSelection.filter((key: string) => key !== option.key);
                        }

                        onChange("regionalBureau", newSelection);
                    }
                }}
                aria-labelledby={regionalBureauFieldId}
                options={regionalBureauOptions}
                onRenderLabel={props => renderLabel(props, regionalBureauFieldId, regionalBureauFieldId, t("helpTexts.includeCategory"))}
                />
            </div>

            <div id="filter-country">
                <Dropdown
                id={countryFieldId}
                className={styles.settingsSeparator}
                label={t("filters.country")}
                selectedKeys={country}
                multiSelect
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => {
                    if (option) {
                        const currentSelection = country || [];
                        let newSelection: string[];

                        if (option.selected) {
                            // Add the option if it's being selected
                            newSelection = [...currentSelection, option.key as string];
                        } else {
                            // Remove the option if it's being deselected
                            newSelection = currentSelection.filter(key => key !== option.key);
                        }

                        onChange("country", newSelection);
                    }
                }}
                aria-labelledby={countryId}
                options={countryOptions}
                onRenderLabel={props => renderLabel(props, countryId, countryFieldId, t("helpTexts.includeCategory"))}
                />
            </div>

            <div id="filter-evaluation-type">
                <Dropdown
                id={evaluationTypeFieldId}
                className={styles.settingsSeparator}
                label={t("filters.evaluationType")}
                selectedKeys={evaluationType}
                multiSelect
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => {
                    if (option) {
                        const currentSelection = evaluationType || [];
                        let newSelection: string[];

                        if (option.selected) {
                            // Add the option if it's being selected
                            newSelection = [...currentSelection, option.key as string];
                        } else {
                            // Remove the option if it's being deselected
                            newSelection = currentSelection.filter(key => key !== option.key);
                        }

                        onChange("evaluationType", newSelection);
                    }
                }}
                aria-labelledby={evaluationTypeId}
                options={evaluationTypeOptions}
                onRenderLabel={props => renderLabel(props, evaluationTypeId, evaluationTypeFieldId, t("helpTexts.includeCategory"))}
                />
            </div>

            <div id="filter-qa-rating">
                <Dropdown
                id={qaRatingFieldId}
                className={styles.settingsSeparator}
                label={t("filters.qarating")}
                selectedKeys={qaRating}
                multiSelect
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => {
                    if (option) {
                        const currentSelection = qaRating || [];
                        let newSelection: string[];

                        if (option.selected) {
                            // Add the option if it's being selected
                            newSelection = [...currentSelection, option.key as string];
                        } else {
                            // Remove the option if it's being deselected
                            newSelection = currentSelection.filter(key => key !== option.key);
                        }

                        onChange("qarating", newSelection);
                    }
                }}
                aria-labelledby={qaRatingId}
                options={qaRatingOptions}
                onRenderLabel={props => renderLabel(props, qaRatingId, qaRatingFieldId, t("helpTexts.includeCategory"))}
                />
            </div>

            <div id="filter-section-types">
                <Dropdown
                id={sectionTypesFieldId}
                className={styles.settingsSeparator}
                label={t("filters.sectionTypes")}
                selectedKeys={sectionTypes}
                multiSelect
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => {
                    if (option) {
                        const currentSelection = sectionTypes || [];
                        let newSelection: string[];

                        if (option.selected) {
                            newSelection = [...currentSelection, option.key as string];
                        } else {
                            newSelection = currentSelection.filter(key => key !== option.key);
                        }

                        onChange("sectionTypes", newSelection);
                    }
                }}
                aria-labelledby={sectionTypesId}
                options={sectionTypeOptions}
                onRenderLabel={props => renderLabel(props, sectionTypesId, sectionTypesFieldId, t("helpTexts.includeCategory"))}
                />
            </div>

            <div id="filter-gef-evaluation">
                <Dropdown
                id={gefEvaluationFieldId}
                className={styles.settingsSeparator}
                label={t("filters.gefEvaluation")}
                selectedKey={gefEvaluation}
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => onChange("gefEvaluation", option?.key || "")}
                aria-labelledby={gefEvaluationId}
                options={gefEvaluationOptions}
                onRenderLabel={props => renderLabel(props, gefEvaluationId, gefEvaluationFieldId, t("helpTexts.includeCategory"))}
                />
            </div>

            <div id="filter-year-from">
                <Dropdown
                id={yearFromFieldId}
                className={styles.settingsSeparator}
                label={t("filters.yearFrom")}
                selectedKey={yearFrom}
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => onChange("yearFrom", option?.key || "")}
                aria-labelledby={yearFromId}
                options={yearOptions}
                onRenderLabel={props => renderLabel(props, yearFromId, yearFromFieldId, t("helpTexts.includeCategory"))}
                onRenderList={(props, defaultRender) => renderYearList(props, defaultRender, yearFrom, (key) => onChange("yearFrom", key))}
                />
            </div>

            <div id="filter-year-to">
                <Dropdown
                id={yearToFieldId}
                className={styles.settingsSeparator}
                label={t("filters.yearTo")}
                selectedKey={yearTo}
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => onChange("yearTo", option?.key || "")}
                aria-labelledby={yearToId}
                options={yearOptions}
                onRenderLabel={props => renderLabel(props, yearToId, yearToFieldId, t("helpTexts.includeCategory"))}
                onRenderList={(props, defaultRender) => renderYearList(props, defaultRender, yearTo, (key) => onChange("yearTo", key))}
                />
            </div>

            {onReset && <DefaultButton className={styles.resetButton} text={t("filters.resetFilters")} onClick={onReset} />}
        </div>
    );
};
